import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Category, CategoryWithSubcategories } from '../types/category';

export const useWebCategories = () => {
  const [mainCategories, setMainCategories] = useState<CategoryWithSubcategories[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all web categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const categoriesRef = collection(db, 'webCategories');
      const q = query(categoriesRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      
      const categories: Category[] = [];
      snapshot.forEach((doc) => {
        categories.push({ id: doc.id, ...doc.data() } as Category);
      });

      // Build hierarchy
      const mainCats: CategoryWithSubcategories[] = categories.filter(cat => !cat.parentId);
      mainCats.forEach(mainCat => {
        mainCat.subcategories = categories.filter(cat => cat.parentId === mainCat.id);
      });

      setMainCategories(mainCats);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching web categories:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Add new category
  const addCategory = async (categoryData: Partial<Category>) => {
    try {
      const categoriesRef = collection(db, 'webCategories');
      const fullPath = categoryData.name || '';
      const docRef = await addDoc(categoriesRef, {
        ...categoryData,
        fullPath,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        productCount: 0
      });
      await fetchCategories();
      return docRef.id;
    } catch (err: any) {
      console.error('Error adding web category:', err);
      throw err;
    }
  };

  // Update category
  const updateCategory = async (id: string, categoryData: Partial<Category>) => {
    try {
      const categoryRef = doc(db, 'webCategories', id);
      await updateDoc(categoryRef, {
        ...categoryData,
        updatedAt: Timestamp.now()
      });
      await fetchCategories();
    } catch (err: any) {
      console.error('Error updating web category:', err);
      throw err;
    }
  };

  // Delete category
  const deleteCategory = async (id: string) => {
    try {
      // Check if category has subcategories
      const hasSubcategories = mainCategories.some(cat => 
        cat.subcategories?.some(sub => sub.id === id) || 
        (cat.id === id && cat.subcategories && cat.subcategories.length > 0)
      );

      if (hasSubcategories) {
        throw new Error('Cannot delete category with subcategories');
      }

      const categoryRef = doc(db, 'webCategories', id);
      await deleteDoc(categoryRef);
      await fetchCategories();
    } catch (err: any) {
      console.error('Error deleting web category:', err);
      throw err;
    }
  };

  // Update product counts for all categories
  const updateCategoryProductCounts = async () => {
    try {
      const productsRef = collection(db, 'products');
      const productsSnapshot = await getDocs(productsRef);
      
      const categoryCounts: Record<string, number> = {};
      
      productsSnapshot.forEach((doc) => {
        const product = doc.data();
        
        // Support both new (webCategoryIds) and old (webCategoryId) format
        const categoryIds: string[] = [];
        if (product.webCategoryIds && Array.isArray(product.webCategoryIds)) {
          categoryIds.push(...product.webCategoryIds);
        } else if (product.webCategoryId) {
          categoryIds.push(product.webCategoryId);
        }
        
        // Count each category
        categoryIds.forEach(categoryId => {
          categoryCounts[categoryId] = (categoryCounts[categoryId] || 0) + 1;
        });
      });

      // Update each category with its product count
      const updatePromises = Object.entries(categoryCounts).map(([categoryId, count]) => {
        const categoryRef = doc(db, 'webCategories', categoryId);
        return updateDoc(categoryRef, { productCount: count });
      });

      await Promise.all(updatePromises);
      await fetchCategories();
    } catch (err: any) {
      console.error('Error updating web category product counts:', err);
      throw err;
    }
  };

  return {
    mainCategories,
    loading,
    error,
    addCategory,
    updateCategory,
    deleteCategory,
    updateCategoryProductCounts,
    refreshCategories: fetchCategories
  };
};
